import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import approvalService from '../../services/approvalService';
import './ApprovalView.css';

const ApprovalView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: approval, isLoading } = useQuery({
    queryKey: ['approval', id],
    queryFn: () => approvalService.getApprovalById(id)
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, data }) => approvalService.approveApproval(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['approval', id]);
      queryClient.invalidateQueries(['approvals']);
      toast.success('Approval approved successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to approve');
    }
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, data }) => approvalService.rejectApproval(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['approval', id]);
      queryClient.invalidateQueries(['approvals']);
      toast.success('Approval rejected successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to reject');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: approvalService.deleteApproval,
    onSuccess: () => {
      toast.success('Approval deleted successfully');
      navigate('/app/approvals');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete approval');
    }
  });

  const handleApprove = () => {
    const notes = prompt('Enter approval notes (optional):');
    approveMutation.mutate({ id, data: { approval_notes: notes || '' } });
  };

  const handleReject = () => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      rejectMutation.mutate({ id, data: { rejection_reason: reason } });
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this approval?')) {
      deleteMutation.mutate(id);
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'Pending': 'badge-warning',
      'Approved': 'badge-success',
      'Rejected': 'badge-danger',
      'Cancelled': 'badge-secondary'
    };
    return classes[status] || 'badge-secondary';
  };

  const getPriorityBadgeClass = (priority) => {
    const classes = {
      'Urgent': 'badge-danger',
      'High': 'badge-warning',
      'Medium': 'badge-info',
      'Low': 'badge-secondary'
    };
    return classes[priority] || 'badge-secondary';
  };

  if (isLoading) {
    return (
      <div className="approval-view-page">
        <div className="loading">Loading approval details...</div>
      </div>
    );
  }

  if (!approval) {
    return (
      <div className="approval-view-page">
        <div className="error">Approval not found</div>
      </div>
    );
  }

  return (
    <div className="approval-view-page">
      <div className="page-header">
        <h1>Approval Details</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/app/approvals')}>
            <i className="fas fa-arrow-left"></i> Back to List
          </button>
          {approval.status === 'Pending' && (
            <>
              <button className="btn btn-success" onClick={handleApprove}>
                <i className="fas fa-check"></i> Approve
              </button>
              <button className="btn btn-danger" onClick={handleReject}>
                <i className="fas fa-times"></i> Reject
              </button>
            </>
          )}
          <button className="btn btn-danger" onClick={handleDelete}>
            <i className="fas fa-trash"></i> Delete
          </button>
        </div>
      </div>

      <div className="approval-details">
        <div className="detail-section">
          <h3>Basic Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Title:</label>
              <div className="detail-value">
                {approval.title}
                {approval.is_urgent && (
                  <span className="badge badge-danger ml-2">URGENT</span>
                )}
              </div>
            </div>

            <div className="detail-item">
              <label>Type:</label>
              <div className="detail-value">{approval.approval_type}</div>
            </div>

            <div className="detail-item">
              <label>Priority:</label>
              <div className="detail-value">
                <span className={`badge ${getPriorityBadgeClass(approval.priority)}`}>
                  {approval.priority}
                </span>
              </div>
            </div>

            <div className="detail-item">
              <label>Status:</label>
              <div className="detail-value">
                <span className={`badge ${getStatusBadgeClass(approval.status)}`}>
                  {approval.status}
                </span>
              </div>
            </div>

            {approval.description && (
              <div className="detail-item full-width">
                <label>Description:</label>
                <div className="detail-value">{approval.description}</div>
              </div>
            )}
          </div>
        </div>

        <div className="detail-section">
          <h3>Request Details</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Requested By:</label>
              <div className="detail-value">{approval.requested_by_name || 'N/A'}</div>
            </div>

            <div className="detail-item">
              <label>Approver:</label>
              <div className="detail-value">{approval.approver_name || 'N/A'}</div>
            </div>

            <div className="detail-item">
              <label>Project:</label>
              <div className="detail-value">{approval.project_name || 'N/A'}</div>
            </div>

            <div className="detail-item">
              <label>Due Date:</label>
              <div className="detail-value">
                {approval.due_date ? new Date(approval.due_date).toLocaleDateString() : 'N/A'}
              </div>
            </div>

            {approval.requested_amount && (
              <div className="detail-item">
                <label>Requested Amount:</label>
                <div className="detail-value">${approval.requested_amount.toFixed(2)}</div>
              </div>
            )}

            {approval.approved_amount && (
              <div className="detail-item">
                <label>Approved Amount:</label>
                <div className="detail-value">${approval.approved_amount.toFixed(2)}</div>
              </div>
            )}
          </div>
        </div>

        {(approval.approval_notes || approval.rejection_reason) && (
          <div className="detail-section">
            <h3>Decision Details</h3>
            <div className="detail-grid">
              {approval.approved_at && (
                <div className="detail-item">
                  <label>Approved At:</label>
                  <div className="detail-value">
                    {new Date(approval.approved_at).toLocaleString()}
                  </div>
                </div>
              )}

              {approval.rejected_at && (
                <div className="detail-item">
                  <label>Rejected At:</label>
                  <div className="detail-value">
                    {new Date(approval.rejected_at).toLocaleString()}
                  </div>
                </div>
              )}

              {approval.approval_notes && (
                <div className="detail-item full-width">
                  <label>Approval Notes:</label>
                  <div className="detail-value">{approval.approval_notes}</div>
                </div>
              )}

              {approval.rejection_reason && (
                <div className="detail-item full-width">
                  <label>Rejection Reason:</label>
                  <div className="detail-value">{approval.rejection_reason}</div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="detail-section">
          <h3>Timestamps</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Created At:</label>
              <div className="detail-value">
                {new Date(approval.created_at).toLocaleString()}
              </div>
            </div>

            <div className="detail-item">
              <label>Updated At:</label>
              <div className="detail-value">
                {new Date(approval.updated_at).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalView;


